import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  IconButton,
  Snackbar,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ReplayIcon from '@mui/icons-material/Replay';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import TextSnippetIcon from '@mui/icons-material/TextSnippet';
import RestoreIcon from '@mui/icons-material/Restore';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import { AiImageGeneration, AiImageOutput, AiImagePendingGeneration } from '../types';
import { downloadAiImageOutput, getAiImageAssetUrl, getAiImageOutputPreviewUrl } from '../utils';
import { getAiImageStatusColor, getAiImageStatusLabel } from '../studioPresentation';
import { describeActualImageCost } from '../costEstimate';
import StudioActionDialog from './StudioActionDialog';

function ActualCostCaption({ generation }: { generation: AiImageGeneration }) {
  const described = describeActualImageCost(generation.cost, generation.usdSekRate);
  if (!described) return null;
  return (
    <Typography
      sx={{
        color: '#fff',
        fontSize: '0.75rem',
        fontWeight: 700,
        lineHeight: 1.35,
        textShadow: '0 1px 2px rgba(0,0,0,0.7)',
      }}
    >
      {described.summary}
    </Typography>
  );
}

interface CurrentGenerationPanelProps {
  generation: AiImageGeneration | null;
  recentGenerations: AiImageGeneration[];
  pendingGenerations: AiImagePendingGeneration[];
  actionLoading: string | null;
  onReuse: (generationId: string, outputId: string) => Promise<void>;
  onUsePrompt: (generation: AiImageGeneration) => void;
  onReuseAll: (generation: AiImageGeneration) => void;
  onUpscale?: (generationId: string, outputId: string) => Promise<void>;
  onEdit?: (generation: AiImageGeneration, output: AiImageOutput) => void;
  onDeleteOutput: (generationId: string, outputId: string) => Promise<void>;
  onReplaceRecipeAsset?: (generationId: string, outputId: string, targetAssetId: string) => Promise<void>;
  onOpenRecipe?: (generation: AiImageGeneration) => Promise<void>;
  recipeTargetAssetId?: string;
  canReplaceRecipeAsset?: boolean;
  maxOutputs?: number;
  onOpenArchive?: () => void;
}

interface FlatOutput {
  generation: AiImageGeneration;
  output: AiImageOutput;
  key: string;
}

function aspectRatioFromValue(value?: string): string {
  const ratioMap: Record<string, string> = {
    '1:1': '1 / 1',
    '3:4': '3 / 4',
    '4:3': '4 / 3',
    '2:3': '2 / 3',
    '3:2': '3 / 2',
    '9:16': '9 / 16',
    '16:9': '16 / 9',
    '5:4': '5 / 4',
    '4:5': '4 / 5',
    '21:9': '21 / 9',
  };

  return ratioMap[value || ''] || '4 / 5';
}

const overlayIconButtonSx = {
  width: 36,
  height: 36,
  minWidth: 36,
  p: 0,
  flexShrink: 0,
  alignSelf: 'center',
  borderRadius: '50%',
  color: '#fff',
  bgcolor: 'rgba(15,23,42,0.55)',
  border: '1px solid rgba(255,255,255,0.18)',
  backdropFilter: 'blur(6px)',
  transition: 'background-color 0.15s ease, transform 0.15s ease',
  '&:hover': { bgcolor: 'rgba(15,23,42,0.78)', transform: 'translateY(-1px)' },
  '&.Mui-focusVisible': { outline: '2px solid #fff', outlineOffset: 2 },
  '& .MuiSvgIcon-root': { fontSize: 18 },
} as const;

const overlayDeleteButtonSx = {
  ...overlayIconButtonSx,
  bgcolor: 'rgba(211,47,47,0.7)',
  '&:hover': { bgcolor: 'rgba(211,47,47,0.9)', transform: 'translateY(-1px)' },
} as const;

const CurrentGenerationPanel: React.FC<CurrentGenerationPanelProps> = ({
  generation,
  recentGenerations,
  pendingGenerations,
  actionLoading,
  onReuse,
  onUsePrompt,
  onReuseAll,
  onUpscale,
  onEdit,
  onDeleteOutput,
  onReplaceRecipeAsset,
  onOpenRecipe,
  recipeTargetAssetId,
  canReplaceRecipeAsset = false,
  maxOutputs = 20,
  onOpenArchive,
}) => {
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<FlatOutput | null>(null);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const allOutputs = useMemo<FlatOutput[]>(() => {
    const seen = new Set<string>();
    const result: FlatOutput[] = [];

    const addFromGeneration = (gen: AiImageGeneration) => {
      for (const output of gen.outputs) {
        const key = `${gen._id}:${output._id}`;
        if (!seen.has(key)) {
          seen.add(key);
          result.push({ generation: gen, output, key });
        }
      }
    };

    if (generation) {
      addFromGeneration(generation);
    }

    for (const gen of recentGenerations) {
      addFromGeneration(gen);
      if (result.length >= maxOutputs) break;
    }

    return result.slice(0, maxOutputs);
  }, [generation, recentGenerations, maxOutputs]);

  const pendingPlaceholders = useMemo(
    () =>
      pendingGenerations.flatMap((pg) =>
        Array.from({ length: pg.count }, (_, i) => ({
          key: `${pg.requestId}-${i}`,
          aspectRatio: pg.aspectRatio,
        }))
      ),
    [pendingGenerations]
  );
  const failedGenerationCost = generation && generation.outputs.length === 0
    ? describeActualImageCost(generation.cost, generation.usdSekRate)
    : null;
  const lightboxOpen = lightboxIndex !== null && lightboxIndex >= 0 && lightboxIndex < allOutputs.length;
  const lightboxItem = lightboxOpen && lightboxIndex !== null ? allOutputs[lightboxIndex] : null;

  const closeSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const handleDownload = async (output: AiImageOutput) => {
    try {
      await downloadAiImageOutput(output);
      setSnackbar({
        open: true,
        message: 'Bilden laddades ned.',
        severity: 'success',
      });
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error?.message || 'Kunde inte ladda ned bilden.',
        severity: 'error',
      });
    }
  };

  const handlePrevInLightbox = () => {
    if (!allOutputs.length || lightboxIndex === null) return;
    setLightboxIndex((lightboxIndex - 1 + allOutputs.length) % allOutputs.length);
  };

  const handleNextInLightbox = () => {
    if (!allOutputs.length || lightboxIndex === null) return;
    setLightboxIndex((lightboxIndex + 1) % allOutputs.length);
  };

  const confirmDeleteOutput = async () => {
    if (!deleteTarget) return;
    const target = deleteTarget;
    setDeleteTarget(null);
    if (
      lightboxItem?.generation._id === target.generation._id
      && lightboxItem.output._id === target.output._id
    ) {
      setLightboxIndex(null);
    }
    await onDeleteOutput(target.generation._id, target.output._id);
  };

  const canReplaceWithOutput = (item: FlatOutput) => Boolean(
    canReplaceRecipeAsset
    && onReplaceRecipeAsset
    && recipeTargetAssetId
    && item.generation.workflow === 'recipe_refresh'
    && item.generation.targetAssetId === recipeTargetAssetId
    && item.output.status !== 'published_to_dam'
  );
  const canOpenRecipe = (item: FlatOutput) => Boolean(
    onOpenRecipe
    && item.generation.workflow === 'recipe_refresh'
    && item.generation.targetAssetId
  );

  return (
    <>
      <Box sx={{ height: '100%', minHeight: 0, overflow: 'auto' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1.5 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
            Senaste bilder
          </Typography>
          {onOpenArchive && (
            <Button size="small" onClick={onOpenArchive} sx={{ textTransform: 'none' }}>
              Arkiv
            </Button>
          )}
        </Box>
        {pendingGenerations.length > 0 && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {pendingGenerations.length === 1
              ? 'Bilderna är på väg.'
              : `${pendingGenerations.length} genereringar i kö.`}
          </Alert>
        )}

        {generation?.errorMessage && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {generation.errorMessage}
          </Alert>
        )}
        {failedGenerationCost && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {failedGenerationCost.summary}
          </Alert>
        )}

        {pendingGenerations.length === 0 && allOutputs.length === 0 && (
          <Box
            sx={{
              height: '100%',
              minHeight: 260,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center',
              px: 3,
              borderRadius: 3,
              border: '1px dashed',
              borderColor: 'rgba(0,0,0,0.08)',
              bgcolor: 'rgba(0,0,0,0.015)',
            }}
          >
            <Typography variant="body2" sx={{ color: 'text.disabled', fontWeight: 500 }}>
              Genererade bilder visas här
            </Typography>
          </Box>
        )}

        {(pendingPlaceholders.length > 0 || allOutputs.length > 0) && (
          <Box
            sx={(theme) => ({
              display: 'grid',
              gap: 1,
              gridTemplateColumns: '1fr',
              [theme.breakpoints.up('md')]: {
                gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
              },
              [theme.breakpoints.up('xl')]: {
                gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
              },
            })}
          >
            {pendingPlaceholders.map((placeholder) => (
              <Box
                key={placeholder.key}
                sx={{
                  borderRadius: 2.5,
                  overflow: 'hidden',
                  position: 'relative',
                  aspectRatio: aspectRatioFromValue(placeholder.aspectRatio),
                  background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 40%, #0f3460 100%)',
                  '@keyframes shimmer': {
                    '0%': { transform: 'translateX(-100%)' },
                    '100%': { transform: 'translateX(100%)' },
                  },
                  '@keyframes pulse': {
                    '0%, 100%': { opacity: 0.6 },
                    '50%': { opacity: 1 },
                  },
                }}
              >
                {/* Shimmer sweep */}
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    overflow: 'hidden',
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '50%',
                      height: '100%',
                      background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.06), transparent)',
                      animation: 'shimmer 2s ease-in-out infinite',
                    },
                  }}
                />
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 1.5,
                  }}
                >
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: '50%',
                      border: '3px solid rgba(255,255,255,0.15)',
                      borderTopColor: 'rgba(255,255,255,0.6)',
                      animation: 'spin 1s linear infinite',
                      '@keyframes spin': {
                        '0%': { transform: 'rotate(0deg)' },
                        '100%': { transform: 'rotate(360deg)' },
                      },
                    }}
                  />
                  <Typography
                    sx={{
                      color: 'rgba(255,255,255,0.7)',
                      fontSize: '0.78rem',
                      fontWeight: 600,
                      letterSpacing: '0.05em',
                      animation: 'pulse 2s ease-in-out infinite',
                    }}
                  >
                    Genererar...
                  </Typography>
                </Box>
              </Box>
            ))}

            {allOutputs.map((item, index) => (
              <Box
                key={item.key}
                sx={{
                  borderRadius: 2.5,
                  overflow: 'hidden',
                  bgcolor: 'grey.50',
                  transition: 'box-shadow 0.2s ease',
                  '&:hover': {
                    boxShadow: '0 8px 25px rgba(0,0,0,0.1)',
                  },
                }}
              >
                <Box
                  sx={{
                    position: 'relative',
                    bgcolor: 'grey.100',
                    cursor: 'zoom-in',
                    '&:hover .current-generation-overlay, &:focus-within .current-generation-overlay': {
                      opacity: 1,
                    },
                  }}
                  onClick={() => setLightboxIndex(index)}
                >
                  <Box
                    component="img"
                    src={getAiImageOutputPreviewUrl(item.output)}
                    alt={item.generation.prompt || 'Genererad bild'}
                    sx={{
                      width: '100%',
                      display: 'block',
                      aspectRatio: item.output.width && item.output.height
                        ? `${item.output.width} / ${item.output.height}`
                        : aspectRatioFromValue(item.generation.aspectRatio),
                      objectFit: 'cover',
                    }}
                  />
                  <Box
                    className="current-generation-overlay"
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'space-between',
                      p: 1.25,
                      background: 'linear-gradient(to top, rgba(0,0,0,0.72), rgba(0,0,0,0.12))',
                      opacity: { xs: 1, md: 0 },
                      '@media (hover: none)': { opacity: 1 },
                      transition: 'opacity 0.18s ease',
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                      <Chip
                        size="small"
                        color={getAiImageStatusColor(item.output.status)}
                        label={getAiImageStatusLabel(item.output.status)}
                        sx={{ bgcolor: 'rgba(255,255,255,0.92)' }}
                      />
                      <Tooltip title="Öppna större bild">
                        <IconButton
                          aria-label="Öppna större bild"
                          size="small"
                          sx={overlayIconButtonSx}
                          onClick={(event) => {
                            event.stopPropagation();
                            setLightboxIndex(index);
                          }}
                        >
                          <OpenInNewIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>

                    <Stack spacing={1} alignItems="flex-start">
                      <ActualCostCaption generation={item.generation} />
                      {canReplaceWithOutput(item) && (
                        <Button
                          variant="contained"
                          color="success"
                          size="small"
                          disabled={actionLoading === `replace-recipe:${item.output._id}`}
                          onClick={(event) => {
                            event.stopPropagation();
                            void onReplaceRecipeAsset?.(item.generation._id, item.output._id, recipeTargetAssetId!);
                          }}
                          sx={{ textTransform: 'none', borderRadius: 2, fontWeight: 700 }}
                        >
                          {actionLoading === `replace-recipe:${item.output._id}` ? 'Ersätter...' : 'Ersätt receptbild'}
                        </Button>
                      )}
                      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        <Tooltip title="Ladda ned">
                          <IconButton
                            aria-label="Ladda ned"
                            size="small"
                            sx={overlayIconButtonSx}
                            onClick={(event) => {
                              event.stopPropagation();
                              void handleDownload(item.output);
                            }}
                          >
                            <DownloadIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      <Tooltip title="Använd prompt">
                        <IconButton
                          aria-label="Använd prompt"
                          size="small"
                          sx={overlayIconButtonSx}
                          onClick={(event) => {
                            event.stopPropagation();
                            onUsePrompt(item.generation);
                          }}
                        >
                          <TextSnippetIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Använd som referens">
                        <IconButton
                          aria-label="Använd som referens"
                          size="small"
                          sx={overlayIconButtonSx}
                          onClick={(event) => {
                            event.stopPropagation();
                            void onReuse(item.generation._id, item.output._id);
                          }}
                        >
                          <ReplayIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Återanvänd allt (prompt + referenser)">
                        <IconButton
                          aria-label="Återanvänd allt"
                          size="small"
                          sx={overlayIconButtonSx}
                          onClick={(event) => {
                            event.stopPropagation();
                            onReuseAll(item.generation);
                          }}
                        >
                          <RestoreIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {onEdit && (
                        <Tooltip title="Redigera">
                          <IconButton
                            aria-label="Redigera"
                            size="small"
                            sx={overlayIconButtonSx}
                            onClick={(event) => {
                              event.stopPropagation();
                              onEdit(item.generation, item.output);
                            }}
                          >
                            <AutoFixHighIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {onUpscale && (
                        <Tooltip title="Skala upp till 4K">
                          <IconButton
                            aria-label="Skala upp till 4K"
                            size="small"
                            sx={overlayIconButtonSx}
                            onClick={(event) => {
                              event.stopPropagation();
                              void onUpscale(item.generation._id, item.output._id);
                            }}
                          >
                            <AutoAwesomeIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {canOpenRecipe(item) && (
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={(event) => {
                            event.stopPropagation();
                            void onOpenRecipe?.(item.generation);
                          }}
                          sx={{
                            color: '#fff',
                            borderColor: 'rgba(255,255,255,0.5)',
                            bgcolor: 'rgba(0,0,0,0.35)',
                            textTransform: 'none',
                            height: 36,
                            px: 1.75,
                            borderRadius: 999,
                            fontWeight: 600,
                            '&:hover': { borderColor: '#fff', bgcolor: 'rgba(0,0,0,0.55)' },
                          }}
                        >
                          Visa recept
                        </Button>
                      )}
                      <Tooltip title="Radera bild">
                        <IconButton
                          aria-label="Radera bild"
                          size="small"
                          sx={overlayDeleteButtonSx}
                          disabled={actionLoading === `delete-output:${item.output._id}`}
                          onClick={(event) => {
                            event.stopPropagation();
                            setDeleteTarget(item);
                          }}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      </Stack>
                    </Stack>
                  </Box>
                </Box>
              </Box>
            ))}
          </Box>
        )}
      </Box>

      <Dialog
        open={lightboxOpen}
        onClose={() => setLightboxIndex(null)}
        onKeyDown={(event) => {
          if (event.key === 'ArrowLeft') {
            event.preventDefault();
            handlePrevInLightbox();
          }
          if (event.key === 'ArrowRight') {
            event.preventDefault();
            handleNextInLightbox();
          }
        }}
        maxWidth={false}
        sx={{
          zIndex: 20000,
          '& .MuiBackdrop-root': { bgcolor: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(8px)' },
        }}
        PaperProps={{
          sx: {
            bgcolor: 'transparent',
            boxShadow: 'none',
            borderRadius: 3,
            overflow: 'visible',
            maxWidth: '92vw',
            maxHeight: '92vh',
          },
        }}
      >
        <DialogContent
          sx={{
            p: 0,
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'visible',
          }}
        >
          <IconButton
            aria-label="Stäng bild"
            onClick={() => setLightboxIndex(null)}
            sx={{
              position: 'absolute',
              top: -12,
              right: -12,
              color: '#fff',
              zIndex: 10,
              bgcolor: 'rgba(255,255,255,0.12)',
              backdropFilter: 'blur(8px)',
              width: 36,
              height: 36,
              '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' },
            }}
          >
            <CloseIcon sx={{ fontSize: 20 }} />
          </IconButton>

          {lightboxItem && allOutputs.length > 1 && (
            <>
              <IconButton
                aria-label="Föregående bild"
                onClick={handlePrevInLightbox}
                sx={{
                  position: 'absolute',
                  left: -24,
                  color: '#fff',
                  zIndex: 10,
                  bgcolor: 'rgba(255,255,255,0.1)',
                  backdropFilter: 'blur(8px)',
                  width: 44,
                  height: 44,
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.22)' },
                }}
              >
                <NavigateBeforeIcon sx={{ fontSize: 28 }} />
              </IconButton>
              <IconButton
                aria-label="Nästa bild"
                onClick={handleNextInLightbox}
                sx={{
                  position: 'absolute',
                  right: -24,
                  color: '#fff',
                  zIndex: 10,
                  bgcolor: 'rgba(255,255,255,0.1)',
                  backdropFilter: 'blur(8px)',
                  width: 44,
                  height: 44,
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.22)' },
                }}
              >
                <NavigateNextIcon sx={{ fontSize: 28 }} />
              </IconButton>
            </>
          )}

          {lightboxItem && (
            <Box sx={{ position: 'relative' }}>
              <Box
                component="img"
                src={getAiImageAssetUrl(lightboxItem.output.publicUrl)}
                alt={lightboxItem.generation.prompt || 'Genererad bild'}
                sx={{ maxWidth: '88vw', maxHeight: '80vh', objectFit: 'contain', borderRadius: 2, display: 'block' }}
              />

              <Stack
                direction="row"
                spacing={0.5}
                sx={{
                  position: 'absolute',
                  left: '50%',
                  bottom: 16,
                  transform: 'translateX(-50%)',
                  bgcolor: 'rgba(0,0,0,0.5)',
                  backdropFilter: 'blur(12px)',
                  borderRadius: 3,
                  px: 1,
                  py: 0.5,
                }}
              >
                <IconButton aria-label="Ladda ned" onClick={() => void handleDownload(lightboxItem.output)} sx={{ color: '#fff', borderRadius: 2, flexDirection: 'column', width: 72, height: 56 }}>
                  <DownloadIcon sx={{ fontSize: 22 }} />
                  <Typography sx={{ fontSize: '0.75rem', fontWeight: 600 }}>Ladda ned</Typography>
                </IconButton>
                <IconButton aria-label="Använd som referens" onClick={() => void onReuse(lightboxItem.generation._id, lightboxItem.output._id)} sx={{ color: '#fff', borderRadius: 2, flexDirection: 'column', width: 72, height: 56 }}>
                  <ReplayIcon sx={{ fontSize: 22 }} />
                  <Typography sx={{ fontSize: '0.75rem', fontWeight: 600 }}>Referens</Typography>
                </IconButton>
                <IconButton aria-label="Använd prompt" onClick={() => onUsePrompt(lightboxItem.generation)} sx={{ color: '#fff', borderRadius: 2, flexDirection: 'column', width: 72, height: 56 }}>
                  <TextSnippetIcon sx={{ fontSize: 22 }} />
                  <Typography sx={{ fontSize: '0.75rem', fontWeight: 600 }}>Prompt</Typography>
                </IconButton>
                <IconButton aria-label="Återanvänd allt" onClick={() => onReuseAll(lightboxItem.generation)} sx={{ color: '#fff', borderRadius: 2, flexDirection: 'column', width: 72, height: 56 }}>
                  <RestoreIcon sx={{ fontSize: 22 }} />
                  <Typography sx={{ fontSize: '0.75rem', fontWeight: 600 }}>Allt</Typography>
                </IconButton>
                {onUpscale && (
                  <IconButton aria-label="Skala upp till 4K" onClick={() => void onUpscale(lightboxItem.generation._id, lightboxItem.output._id)} sx={{ color: '#fff', borderRadius: 2, flexDirection: 'column', width: 88, height: 56 }}>
                    <AutoAwesomeIcon sx={{ fontSize: 22 }} />
                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 600 }}>Skala upp</Typography>
                  </IconButton>
                )}
              </Stack>

              <Typography
                variant="caption"
                sx={{
                  position: 'absolute',
                  top: 12,
                  left: 12,
                  color: '#fff',
                  bgcolor: 'rgba(0,0,0,0.4)',
                  backdropFilter: 'blur(8px)',
                  borderRadius: 1.5,
                  px: 1.5,
                  py: 0.5,
                  fontSize: '0.72rem',
                  fontWeight: 600,
                }}
              >
                {lightboxIndex !== null ? `${lightboxIndex + 1} / ${allOutputs.length}` : ''}
              </Typography>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      <StudioActionDialog
        open={Boolean(deleteTarget)}
        title="Radera bild"
        description="Radera den här bilden från Athena-arkivet? Publicerade DAM-assets påverkas inte. Det här tar bara bort den valda bilden, inte hela generationen."
        confirmLabel="Radera bild"
        confirmColor="error"
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => { void confirmDeleteOutput(); }}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={closeSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={closeSnackbar} severity={snackbar.severity} variant="filled" sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default CurrentGenerationPanel;
